import { useEffect, useMemo, useState } from 'react';

import { LocalStorageKey } from '@neo.tax/shared/enums';
import { AccountingToken, GetCompanyQuery, PayrollToken } from '@neo.tax/shared/graphql';
import { useProviderTokensAndSyncedProviders, useCurrentCompanyId } from '@neo.tax/shared/hooks';

import { ConnectAccountingProviderSlideProps, ConnectPayrollProviderSlideProps } from '../types';
import { useDeleteTokensByCompanyAndProvider } from './useDeleteTokensByCompanyAndProvider';

type UseProviderConnectStateProps = (ConnectAccountingProviderSlideProps | ConnectPayrollProviderSlideProps) & {
  onFail?: () => void;
  fiscalYearId: string;
};

export interface UseProviderConnectState {
  tokenRegistered: boolean;
  synced: boolean;
  loading: boolean;
  payrollToken?: GetCompanyQuery['getCompany']['payrollTokens'][number];
  accountingToken?: GetCompanyQuery['getCompany']['accountingTokens'][number];
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
}

export const useProviderConnectState = ({
  fiscalYearId,
  onFail,
  // We specifically only want the provider definition; no other props
  ...providerDefinition
}: UseProviderConnectStateProps): UseProviderConnectState => {
  const companyId = useCurrentCompanyId();
  // Initial state should be loading until tokens are eagerly deleted
  const [loading, setLoading] = useState(true);

  const [deleteTokens, { loading: deletingTokens }] = useDeleteTokensByCompanyAndProvider({
    companyId,
    ...providerDefinition,
  });
  const { providerName, providerType } = providerDefinition;

  const {
    accountingTokens,
    loading: loadingTokensAndSyncedProviders,
    payrollTokens,
    startPolling,
    stopPolling,
    syncedProviders,
  } = useProviderTokensAndSyncedProviders({ fiscalYearId });

  useEffect(() => {
    // Eagerly delete tokens and force the user to re-auth to prevent errors from expired tokens
    // Once we improve backend error messages, we can remove this and handle expired tokens gracefully
    (async (): Promise<void> => {
      await deleteTokens();
      setLoading(false);
    })();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkIfConnectPayrollFailed = (): void => {
      const localStorageKey =
        providerType === 'payroll'
          ? LocalStorageKey.SESSION_CONNECTED_PAYROLL
          : LocalStorageKey.SESSION_CONNECTED_ACCOUNTING;
      const item = localStorage.getItem(localStorageKey);
      if (!item || !onFail) {
        return;
      }
      onFail();
    };

    window.addEventListener('storage', checkIfConnectPayrollFailed);

    return (): void => {
      window.removeEventListener('storage', checkIfConnectPayrollFailed);
    };
  }, [onFail, providerType]);

  return useMemo(() => {
    const baseConfig = {
      loading: loading || deletingTokens || loadingTokensAndSyncedProviders,
      startPolling,
      stopPolling,
    };

    if (providerType === 'payroll') {
      const payrollToken = payrollTokens?.find(
        // payrollToken.payrollProvider is a string, providerName is a PayrollProvider enum
        (token: PayrollToken) => token.payrollProvider?.toLowerCase() === providerName.toLowerCase(),
      );
      const tokenRegistered = !!payrollToken;
      const synced =
        syncedProviders?.payrollProviders?.findIndex((provider) => provider.providerName === providerName) !== -1;
      return { ...baseConfig, tokenRegistered, synced, payrollToken };
    }

    const accountingToken = accountingTokens?.find(
      (token: AccountingToken) => token.accountingProvider === providerName,
    );
    const tokenRegistered = !!accountingToken;
    const synced =
      syncedProviders?.accountingProviders?.findIndex((provider) => provider.providerName === providerName) !== -1;

    return { ...baseConfig, tokenRegistered, synced, accountingToken };
  }, [
    accountingTokens,
    deletingTokens,
    loading,
    loadingTokensAndSyncedProviders,
    payrollTokens,
    providerName,
    providerType,
    startPolling,
    stopPolling,
    syncedProviders?.accountingProviders,
    syncedProviders?.payrollProviders,
  ]);
};
